import React, { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useSimpleAuth } from '../../features/auth/SimpleAuthContext'

/**
 * GlobalNavigation - Menú hamburguesa neobrutalista global
 * Se muestra en todas las páginas de la aplicación excepto en la home
 */
const GlobalNavigation = () => {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const { isAdmin } = useSimpleAuth()

  // Ocultar menú en la página principal
  if (location.pathname === '/' || location.pathname === '') {
    return null
  }

  // Configuración de rutas globales
  const allLinks = [
    {
      name: 'Eventos',
      path: '/comunidad/eventos',
      color: '#FFE600', // amarillo
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <rect x="3" y="4" width="18" height="18" rx="0" ry="0"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      )
    },
    {
      name: 'Spots',
      path: '/comunidad/spots',
      color: '#6BAF7B', // verde hierba
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <circle cx="12" cy="12" r="10"></circle>
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      )
    },
    {
      name: 'Grupos',
      path: '/comunidad/grupos',
      color: '#00D4FF', // azul/turquesa
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      )
    },
    {
      name: 'Chat',
      path: '/comunidad/chat',
      color: '#7FFF00', // verde lima
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      )
    },
    {
      name: 'Tiendas',
      path: '/shops',
      color: '#FF6B9D', // rosa
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      )
    },
    {
      name: 'Academias',
      path: '/academia',
      color: '#FF8C42', // naranja coral
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
          <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
        </svg>
      )
    },
    {
      name: 'Donar',
      path: '/donate',
      color: '#FF6B9D', // rosa
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
        </svg>
      )
    },
    {
      name: 'Perfil',
      path: '/profile',
      color: '#3BC6C6', // turquesa cálido
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      )
    },
    {
      name: 'Administración',
      path: '/admin',
      color: '#A855F7', // púrpura
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
      )
    }
  ]

  // Filtrar enlaces según el rol del usuario
  const links = useMemo(() => {
    return allLinks.filter(link => {
      // Solo mostrar admin si el usuario tiene el rol
      if (link.path === '/admin') {
        return isAdmin
      }
      return true
    })
  }, [isAdmin])

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lexend+Mega:wght@700;900&display=swap');

        /* FAB BUTTON - Botón flotante global */
        .global-nav-fab {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 1000;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          border: 4px solid #000;
          border-radius: 0;
          cursor: pointer;
          box-shadow: 6px 6px 0 0 #000;
          transition: transform 0.1s ease, box-shadow 0.1s ease, background 0.1s ease;
        }

        @media (min-width: 370px) {
          .global-nav-fab {
            width: 60px;
            height: 60px;
            bottom: 22px;
            right: 22px;
          }
        }

        .global-nav-fab:hover {
          transform: translate(-2px, -2px);
          box-shadow: 8px 8px 0 0 #000;
        }

        .global-nav-fab:active {
          transform: translate(2px, 2px);
          box-shadow: 2px 2px 0 0 #000;
        }

        .global-nav-fab.active {
          background: #FFE600;
          box-shadow: 4px 4px 0 0 #000;
          transform: translate(1px, 1px);
        }

        /* MOBILE MENU - Menú desplegable desde el FAB */
        .global-nav-menu {
          position: fixed;
          bottom: 85px;
          right: 20px;
          z-index: 999;
          background: #fff;
          border: 4px solid #000;
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          box-shadow: 6px 6px 0 0 #000;
          min-width: 180px;
          max-width: calc(100vw - 48px);
          max-height: 70vh;
          overflow-y: auto;
        }

        @media (min-width: 370px) {
          .global-nav-menu {
            min-width: 200px;
            right: 22px;
            padding: 16px;
          }
        }

        .global-nav-link {
          font-family: 'Lexend Mega', sans-serif;
          font-size: 0.85rem;
          font-weight: 900;
          text-decoration: none;
          color: #000;
          padding: 10px 12px;
          border: 3px solid #000;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 3px 3px 0 0 #000;
          transition: transform 0.1s ease, box-shadow 0.1s ease;
          background: #fff;
          text-transform: uppercase;
        }

        @media (min-width: 370px) {
          .global-nav-link {
            font-size: 0.95rem;
            padding: 12px 16px;
            gap: 12px;
          }
        }

        .global-nav-link:hover {
          transform: translate(-1px, -1px);
          box-shadow: 4px 4px 0 0 #000;
        }

        .global-nav-link:active {
          transform: translate(2px, 2px);
          box-shadow: 1px 1px 0 0 #000;
        }

        .global-nav-link.active-link {
          background: #FFE600;
          font-weight: 900;
        }

        @media (min-width: 768px) {
          .global-nav-fab {
            width: 72px;
            height: 72px;
            bottom: 32px;
            right: 32px;
          }

          .global-nav-menu {
            min-width: 240px;
            bottom: 120px;
            right: 32px;
          }
        }
      `}</style>

      {/* Botón flotante (FAB) */}
      <button
        className={`global-nav-fab ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Menu"
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="square">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="square">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        )}
      </button>

      {/* Menú desplegable */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="global-nav-menu"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className="global-nav-menu"
          >
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`global-nav-link ${isActive(link.path) ? 'active-link' : ''}`}
                style={{
                  backgroundColor: isActive(link.path) ? link.color : '#fff',
                }}
              >
                {link.icon}
                {link.name}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default GlobalNavigation
